import { HomeReview, ReviewTemplate } from '../types';

export type ReviewTemplateCacheDependencies = {
  cacheService: any;
  database: any;
  onlineApi: {
    getTemplates(): Promise<ReviewTemplate[]>;
    getTemplateDetails(templateId: number): Promise<ReviewTemplate>;
    getReviewTemplate(reviewId: number): Promise<ReviewTemplate>;
  };
  ensureOnlineState: () => Promise<boolean>;
  getIsOnline: () => boolean;
  isWeb: () => boolean;
};

export class ReviewTemplateCacheService {
  constructor(private readonly deps: ReviewTemplateCacheDependencies) {}

  async getTemplates(): Promise<ReviewTemplate[]> {
    try {
      if (this.deps.cacheService?.getTemplates) {
        const templates = await this.deps.cacheService.getTemplates();
        if (templates && templates.length > 0) {
          return templates;
        }
      }

      if (this.deps.getIsOnline()) {
        return this.deps.onlineApi.getTemplates();
      }

      return [];
    } catch (error) {
      return [];
    }
  }

  async getTemplateDetails(
    templateId: number,
    options?: { preferFresh?: boolean }
  ): Promise<ReviewTemplate> {
    const preferFresh = options?.preferFresh ?? false;

    let cachedTemplate: ReviewTemplate | undefined;
    if (this.deps.cacheService?.getTemplates) {
      const templates = await this.deps.cacheService.getTemplates();
      cachedTemplate = templates.find((template: ReviewTemplate) => template.id === templateId);
    }

    if (!preferFresh && cachedTemplate) {
      return cachedTemplate;
    }

    if (this.deps.getIsOnline()) {
      try {
        const fullTemplate = await this.deps.onlineApi.getTemplateDetails(templateId);
        await this.cacheTemplateDetails(fullTemplate);
        return fullTemplate;
      } catch (networkError) {
        if (cachedTemplate) {
          return cachedTemplate;
        }
        throw networkError;
      }
    }

    if (cachedTemplate) {
      return cachedTemplate;
    }

    throw new Error(`Template ${templateId} not found in cache and offline`);
  }

  async ensureReviewTemplate(review: HomeReview): Promise<void> {
    if (!review?.templateId || !review?.id) {
      return;
    }

    if (review.template && this.isTemplateComplete(review.template)) {
      return;
    }

    if (!this.deps.isWeb() && !this.deps.getIsOnline()) {
      return;
    }

    try {
      const template = await this.deps.onlineApi.getReviewTemplate(review.id);
      review.template = template;

      if ((review as any).reviewData) {
        (review as any).reviewData.template = template;
      }

      if (this.deps.database?.saveReviewTemplateSnapshot) {
        this.deps.database
          .saveReviewTemplateSnapshot(review.id, template)
          .catch((err: any) =>
            console.error('[ReviewTemplateCacheService] Failed to cache template snapshot:', err)
          );
      }
    } catch (error) {
      console.error('[ReviewTemplateCacheService] Failed to ensure template data:', error);
    }
  }

  async refreshTemplateCache(): Promise<void> {
    if (this.deps.isWeb() || !this.deps.cacheService?.refreshTemplates) {
      return;
    }

    if (!(await this.deps.ensureOnlineState())) {
      return;
    }

    try {
      await this.deps.cacheService.refreshTemplates();
    } catch (error) {
      console.warn('[ReviewTemplateCacheService] Failed to refresh template cache:', error);
    }
  }

  private isTemplateComplete(template: ReviewTemplate): boolean {
    if (!template.categories || template.categories.length === 0) {
      return false;
    }

    return template.categories.some(
      category => category.items && category.items.length > 0
    );
  }

  private async cacheTemplateDetails(template: ReviewTemplate): Promise<void> {
    if (!this.deps.database?.cacheTemplates) {
      return;
    }

    try {
      await this.deps.database.cacheTemplates([template], 24);
      await this.deps.database.updateCacheMetadata?.('templates', new Date());
    } catch (cacheError) {
      // Template detail reads should not fail because the offline cache failed.
    }
  }
}
